/**
 * Usability guard for imperative MapLibre layer operations.
 *
 * # Why this exists (the "Reset to profile" crash)
 *
 * Every layer helper in this directory guards its work with
 * ``map.getLayer(id)`` / ``map.getSource(id)`` before mutating — e.g.
 * :func:`snapshot-layer-impl.detachSnapshotLayer` does
 * ``if (map.getLayer(SNAPSHOT_LAYER_ID)) map.removeLayer(...)``. That guard
 * is **not safe against a removed map**: MapLibre implements
 * ``Map.getLayer(id)`` as ``return this.style.getLayer(id)`` and
 * ``Map.setPaintProperty(...)`` as ``return this.style.setPaintProperty(...)``,
 * and ``Map.remove()`` does ``delete this.style``. So calling ``getLayer`` on a
 * map whose style is gone throws the exact reported error —
 * ``TypeError: Cannot read properties of undefined (reading 'getLayer')`` — and
 * the "guard" is itself the throwing call.
 *
 * This bites on the **"Reset to profile"** flow: re-applying a Profile calls
 * :func:`multi-pane-workspace.MultiPaneWorkspace`'s ``applyLayout``, which does
 * ``dockview.clear()`` + ``dockview.fromJSON(...)`` — tearing down the Map
 * panel (so :class:`Map` unmounts and :func:`use-map.useMap` runs
 * ``map.remove()``) and re-mounting a fresh one. During that teardown / remount
 * a layer effect or cleanup can run a ``getLayer`` / ``setPaintProperty`` /
 * ``setLayoutProperty`` / ``getStyle`` against a map that is **removed**
 * (``style`` deleted), and the app crashes into the React error boundary.
 *
 * # What actually throws — *removed*, not merely *loading*
 *
 * The state that makes a layer op throw is a **removed** map (``style``
 * deleted). ``addSource`` / ``addLayer`` / ``getLayer`` / ``getSource`` /
 * ``setPaintProperty`` / ``setLayoutProperty`` / ``removeLayer`` are all
 * **valid while the style's sources are still loading** (``isStyleLoaded()
 * === false``) — MapLibre applies them as the tiles settle. So the unsafe
 * signal is precisely ``map.style == null`` (removed); ``isStyleLoaded() ===
 * false`` (loading) is **not** unsafe and must **not** block layer mutation.
 *
 * **One narrower window is different — "loading" before the style JSON has
 * even been parsed** (2026-10-07). ``Style.loadJSON`` defers its ``_load`` to
 * the next animation frame, and until that frame runs every style *mutator*
 * (``addLayer`` / ``removeLayer`` / ``addSource`` / ``removeSource`` /
 * ``setPaintProperty`` …) throws ``"Style is not done loading."`` from
 * ``Style._checkLoaded``, and ``Map.getStyle()`` returns ``undefined``
 * (``Style.serialize`` bails on the same flag — the d.ts says
 * ``StyleSpecification``, the implementation disagrees). On the healthy path
 * that frame runs long before ``load``, so nothing post-load ever sees it.
 * The REQ-UI-166 watchdog is what exposes it: a map mounted in a **hidden
 * browser tab** gets no animation frames at all, so ``load`` never arrives,
 * the watchdog promotes the map after its deadline, and the post-load theme
 * effect ran :func:`use-map-theme.rebuildBasemap` into ``getStyle().layers``
 * — the "Cannot read properties of undefined (reading 'layers')" crash that
 * took the whole route to the error boundary whenever an operator returned
 * to the tab. :func:`isStyleReady` detects that window and
 * :func:`runWhenStyleReady` defers the work to MapLibre's ``style.load``,
 * which fires as soon as the tab gets its frame.
 *
 * This distinction is the fix for the **clusters-don't-render-at-low-zoom**
 * regression. :func:`cluster-layer-impl.attachClusterLayers` runs through the
 * :class:`cluster-layer.ClusterLayer` attach effect, which keys on the
 * ``hasSnapshot`` *boolean* — so it fires exactly **once**, when the snapshot
 * first arrives (the signal that :class:`snapshot-layer.SnapshotLayer` created
 * the source). That single run commonly lands inside the style-load window
 * (the snapshot + its source are mid-load). An earlier, over-strict guard that
 * also required ``isStyleLoaded()`` early-returned on that one run and, because
 * the effect never re-runs on later snapshot-object updates or on zoom, blocked
 * the cluster circle + count layers **permanently** — so at zoom ≤
 * ``CLUSTER_MAX_ZOOM`` (where the snapshot symbol layer filters clustered
 * features out via ``["!", ["has", "point_count"]]``) nothing painted. The
 * individual-glyph layer was spared only because its data effect keys on the
 * snapshot *object* and retries every update until the style settles. Gating on
 * "present + not removed" (this guard) lets the one-shot cluster attach succeed
 * during load, which is the correct, valid behavior.
 *
 * # The guard
 *
 * :func:`isMapUsable` returns ``true`` when the map exists and still has its
 * ``style`` (i.e. has NOT been ``remove()``-d) — regardless of whether the
 * style has finished loading. Layer helpers call it as their leading line so
 * they **no-op safely** only when the map has been torn down — fail-safe per
 * CLAUDE.md §8.1 (a removed map simply isn't touched, rather than crashing the
 * operator's whole workspace), while a still-loading map is treated as usable
 * so attaches/modifications/detaches that race the load window are not lost.
 *
 * ## Why not gate on ``isStyleLoaded()``
 *
 * No layer op in this directory crashes on a *loading* map; the attach/detach
 * bodies already guard "layer/source not added yet" with their own
 * ``getLayer`` / ``getSource`` checks, and the only query-shaped helper
 * (:func:`cluster-layer-impl.computeClusterCounts` → ``queryRenderedFeatures``)
 * simply returns empty before the style settles and is recomputed on
 * ``moveend`` / ``sourcedata`` — empty-then-correct, never a crash. Requiring
 * ``isStyleLoaded()`` therefore buys nothing and actively breaks the one-shot
 * cluster attach above. (MapLibre's ``isStyleLoaded()`` is also itself
 * ``this.style ? this.style.loaded() : warnOnce("There is no style added to the
 * map.")`` — safe on a removed map but noisy; another reason not to call it on
 * the hot teardown path.)
 *
 * ## Permissive when the removal signal is absent (test stubs)
 *
 * The guard returns ``true`` (usable) unless it finds **positive evidence** the
 * map is removed — i.e. it exposes the real MapLibre :meth:`Map.isStyleLoaded`
 * method (marking it a faithful map surface) *and* its ``style`` field is gone.
 * A bare unit-test stub that implements only the handful of layer methods
 * (``getLayer`` / ``addLayer`` / …) and exposes **no** ``isStyleLoaded`` is
 * therefore treated as usable, so the existing direct-call layer tests (which
 * never modelled ``style`` / ``isStyleLoaded``) keep exercising the
 * attach/detach bodies unchanged. The live app and any test that wants to
 * exercise the removed path supply a faithful map with ``isStyleLoaded`` and a
 * deleted ``style``.
 *
 * Cross-references:
 *   - REQ-UI-002 (MapLibre layer lifecycle).
 *   - REQ-UI-010 / REQ-UI-011 (cluster vs individual at low zoom — the
 *     regression this relaxation fixes).
 *   - REQ-UI-052 / REQ-UI-077 (layout apply / Profile reset — the removed-map
 *     crash this still defends against).
 *   - ADR-016 §4 (WebGL rendering — imperative layer lifetimes).
 *   - ADR-024 §6 (dockview layout apply tears the Map panel down + back up).
 *   - CLAUDE.md §8.1 (fail-safe; operator-visibility) / §5.4 (no log noise).
 */
import type { Map as MapLibreMap } from "maplibre-gl";

/**
 * The subset of the MapLibre :class:`Map` surface this guard probes. Declared
 * structurally (not via the full ``Map`` type) so the unit tests can pass a
 * tiny stub, and so a ``null`` / ``undefined`` map (defensive — the layer
 * helpers are typed non-null, but a teardown race can still surface one) is
 * accepted without a cast.
 */
interface MapUsableProbe {
  /**
   * MapLibre deletes this field in ``Map.remove()`` (``delete this.style``), so
   * its presence is the "map has not been removed" signal. Typed ``unknown``
   * because the internal ``Style`` type is not part of the public surface — we
   * only test truthiness.
   */
  readonly style?: unknown;
  /**
   * MapLibre's ``Map.isStyleLoaded()``. Probed only as a **structural marker**
   * that this is a faithful map surface (vs a bare layer-method stub) — its
   * boolean result is intentionally NOT consulted, because a loading style is
   * still usable for layer mutation (see the module docstring). Optional so a
   * minimal stub can omit it (treated as usable when absent).
   */
  isStyleLoaded?: () => boolean;
}

/**
 * Is ``map`` in a state where imperative layer operations
 * (``getLayer`` / ``addLayer`` / ``setPaintProperty`` / ``setLayoutProperty``
 * / ``getSource`` / ``setData`` …) are valid — i.e. the map exists and has
 * **not** been ``remove()``-d?
 *
 * Returns ``false`` (NOT usable) only on **positive evidence** the map is torn
 * down:
 *   - ``map`` is ``null`` / ``undefined``; or
 *   - ``map`` exposes the real :meth:`Map.isStyleLoaded` method (so it is a
 *     faithful map surface, not a bare test stub) AND its ``style`` has been
 *     deleted (``Map.remove()`` ran).
 *
 * A map whose style is still **loading** (``style`` present, ``isStyleLoaded()
 * === false``) returns ``true`` — layer mutations are valid during load and
 * MapLibre applies them when the style settles. This is deliberate: the
 * one-shot cluster attach (:func:`cluster-layer-impl.attachClusterLayers` via
 * :class:`cluster-layer.ClusterLayer`'s ``hasSnapshot``-keyed effect) commonly
 * runs inside that window and must not be dropped, or clusters never paint at
 * low zoom (REQ-UI-010 / REQ-UI-011). See the module docstring for the full
 * rationale and the MapLibre internals this distinction rests on.
 *
 * Otherwise returns ``true`` — including for a minimal test stub that exposes
 * no ``isStyleLoaded`` method (see the module docstring "Permissive when the
 * removal signal is absent"), so the existing direct-call layer tests keep
 * exercising the attach/detach bodies.
 *
 * Used as the leading guard in every ``attach*`` / ``detach*`` / modify layer
 * helper so a removed map is a safe no-op instead of a crash.
 *
 * @param map - The MapLibre map, or ``null`` / ``undefined`` (defensive).
 * @returns ``true`` when layer mutations are safe to perform.
 *
 * Cross-references:
 *   - REQ-UI-002; REQ-UI-010 / REQ-UI-011; ADR-016 §4; CLAUDE.md §8.1.
 */
/**
 * The subset of the map surface :func:`isStyleReady` / :func:`runWhenStyleReady`
 * touch — optional members so a bare test stub is treated as ready.
 */
interface StyleReadyProbe {
  getStyle?: () => unknown;
  once?: (type: string, listener: () => void) => unknown;
  off?: (type: string, listener: () => void) => unknown;
}

/**
 * Has ``map``'s style document been parsed, so its mutators will not throw?
 *
 * ``Map.getStyle()`` returns ``undefined`` exactly while ``Style._loaded`` is
 * false — the same flag ``Style._checkLoaded`` throws on — which makes it the
 * one probe that answers this without reaching into MapLibre's privates. A
 * map (or stub) with no ``getStyle`` is treated as ready, matching
 * :func:`isMapUsable`'s "permissive when the signal is absent" stance so bare
 * layer-test stubs keep driving the real bodies.
 *
 * @param map - The MapLibre map, or a test double.
 * @returns ``true`` when style mutation is safe (or cannot be judged).
 */
export function isStyleReady(map: MapLibreMap): boolean {
  const probe = map as unknown as StyleReadyProbe;
  if (typeof probe.getStyle !== "function") {
    return true;
  }
  return probe.getStyle() !== undefined;
}

/**
 * Run ``work`` now if the style is parsed, else once MapLibre fires
 * ``style.load``.
 *
 * Shaped for a React effect body: it returns the cleanup that removes the
 * pending listener, so an effect that re-runs (theme flip, descriptor change)
 * or unmounts before the style parses never fires stale work. A stub without
 * ``once`` runs the work immediately — there is no event to wait for.
 *
 * @param map - The MapLibre map.
 * @param work - What to do against the parsed style.
 * @returns A disposer; a no-op when the work already ran.
 *
 * Cross-references:
 *   - REQ-UI-166 (the watchdog promotion that opens the window).
 *   - REQ-UI-040 / REQ-UI-091 (the theme / basemap effect this guards).
 */
export function runWhenStyleReady(map: MapLibreMap, work: () => void): () => void {
  const probe = map as unknown as StyleReadyProbe;
  if (isStyleReady(map) || typeof probe.once !== "function") {
    work();
    return () => undefined;
  }
  probe.once("style.load", work);
  return () => {
    probe.off?.("style.load", work);
  };
}

export function isMapUsable(map: MapLibreMap | null | undefined): boolean {
  // Read through the structural probe so the field/method access is the only
  // contact with the map — we never call a method that dereferences
  // ``this.style`` until we've confirmed ``style`` is present (so we never trip
  // the removed-map ``TypeError``, nor MapLibre's "no style added" warnOnce).
  const probe = map as MapUsableProbe | null | undefined;
  if (probe == null) {
    return false;
  }
  // No readiness API (a minimal unit-test stub) → assume usable: there is no
  // evidence of removal to act on, and the existing layer tests pass bare stubs
  // that must continue to drive the attach/detach bodies. (The presence of the
  // real ``isStyleLoaded`` method is used only as the "faithful surface" marker
  // below; its boolean result is never consulted — a loading style is usable.)
  if (typeof probe.isStyleLoaded !== "function") {
    return true;
  }
  // Real MapLibre surface. ``style`` is deleted by ``Map.remove()``; its
  // absence is the sole unsafe signal (every layer method would throw on
  // ``this.style``). A present ``style`` — loaded OR still loading — is usable.
  return probe.style != null;
}
